import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Users, BarChart2, Layout, TrendingUp, Settings, ChevronLeft, ChevronRight,
  Activity, Bot, Terminal, UserCircle, Grid, Zap, LineChart, MessageSquare,
  BarChart, HelpCircle, Bug, Target, Image, Globe, CheckSquare, FileText, Building2,
  Monitor, PlayCircle, Brain, User, CreditCard, Database, BookOpen, Shield,
  FolderOpen, Plus, Code, Layers, MessageCircle, Key, Package, Edit3, Search, Video, Sparkles, Trophy,
  Workflow, Upload, Trash2, Wrench, Factory, Briefcase, DollarSign, Gauge, Wand2, Inbox, Lightbulb,
  ShoppingBag, Calendar
} from 'lucide-react';
import { useAuth } from '@/context/auth-context';
import { useTheme } from '@/components/theme-provider';
import { useOfferingType } from '@/hooks/useOfferingType';
import type { OfferingType } from '@/types/offering';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface SidebarBadge {
  count: number;
  variant: 'blue' | 'green' | 'red' | 'yellow';
}

interface SidebarSubItem {
  label: string;
  to?: string;  // Optional for header items
  badge?: SidebarBadge;
  avatar?: string;
  icon?: React.ElementType;
  isHeader?: boolean;
  visibleFor?: OfferingType[] | 'all';
}

interface SidebarItem {
  icon?: React.ElementType;
  label: string;
  to?: string;
  isSection?: boolean;
  subItems?: SidebarSubItem[];
  hint?: string;
  visibleFor?: OfferingType[] | 'all';
}

function filterSidebarByOfferingType(items: SidebarItem[], offeringType: OfferingType): SidebarItem[] {
  return items
    .filter(item => !item.visibleFor || item.visibleFor === 'all' || item.visibleFor.includes(offeringType))
    .map(item => {
      if (!item.subItems) return item;
      const filteredSubs = item.subItems.filter(
        sub => !sub.visibleFor || sub.visibleFor === 'all' || sub.visibleFor.includes(offeringType)
      );
      return { ...item, subItems: filteredSubs };
    });
}

// Site Owner Menu Items
const siteOwnerItems: SidebarItem[] = [
  // Dashboard
  {
    label: 'Dashboard',
    isSection: true,
  },
  {
    icon: Home,
    label: 'Home',
    to: '/dashboard',
    hint: 'Your command center - see how your site is performing',
  },
  // About the Business
  {
    label: 'About the Business',
    isSection: true,
  },
  {
    icon: TrendingUp,
    label: 'The Sector',
    to: '/about-business/sector',
    hint: 'Industry insights and trends for your business',
  },
  {
    icon: Package,
    label: 'The Offering',
    to: '/about-business/offering',
    hint: 'Your products, services, and content catalog',
  },
  {
    icon: Sparkles,
    label: 'Discovery',
    to: '/discovery',
    hint: 'Natural language search across your products and knowledge',
  },
  {
    icon: DollarSign,
    label: 'Demand Research',
    to: '/demand-research',
    hint: 'Category demand vs content readiness analysis',
  },
  {
    icon: MessageCircle,
    label: 'Conversation Intel',
    to: '/conversation-intelligence',
    hint: 'Community conversations, sentiment, opportunities & experts',
  },
  // Services (service & mixed sites only)
  {
    icon: Briefcase,
    label: 'Services',
    visibleFor: ['service', 'mixed'] as const,
    subItems: [
      { label: 'Service Catalog', to: '/service/catalog', icon: Package },
      { label: 'Bookings', to: '/service/bookings', icon: Calendar },
      { label: 'Integrations', to: '/service/integrations', icon: Workflow },
      { label: 'Lead Inbox', to: '/service/leads', icon: Inbox },
    ],
  },
  // Agents
  {
    label: 'Agents',
    isSection: true,
  },
  {
    icon: UserCircle,
    label: 'Meet the Crew',
    to: '/crew',
    hint: 'Meet your AI assistants and see what they\'re doing',
  },
  {
    icon: MessageSquare,
    label: 'Agents Insights',
    to: '/insights/messages',
    hint: 'Discover what your AI agents learned about visitors',
  },
  {
    icon: Inbox,
    label: 'Action Inbox',
    to: '/insights/inbox',
    hint: 'Review and act on AI agent suggestions',
  },
  {
    icon: MessageCircle,
    label: 'Agent Conversations',
    to: '/agent-conversations',
    hint: 'Read actual conversations between visitors and AI',
  },
  // Customers & Growth
  {
    label: 'Customers & Growth',
    isSection: true,
  },
  {
    icon: BarChart2,
    label: 'Analytics & Chatbot',
    to: '/analytics',
    hint: 'Track web traffic, chatbot conversations, and engagement metrics',
  },
  {
    icon: Users,
    label: 'Customer Segments',
    to: '/analytics/customer-segments',
    hint: 'See who your visitors are - buyers, browsers, researchers',
  },
  {
    icon: Layers,
    label: 'Segment Management',
    to: '/analytics/segment-management',
    hint: 'Define and customize your visitor groups',
  },
  {
    icon: BarChart2,
    label: 'Segment Analytics',
    to: '/analytics/segment-analytics',
    hint: 'Compare how different visitor groups perform',
  },
  {
    icon: Layout,
    label: 'Experiences',
    to: '/session-replays',
    hint: 'Watch how real visitors navigate your site',
  },
  {
    icon: Brain,
    label: 'Behavior',
    to: '/behavior',
    hint: 'Understand what visitors do and why they do it',
  },
  {
    icon: TrendingUp,
    label: 'Growth',
    to: '/growth',
    hint: 'Find opportunities to grow your business',
  },
  // Knowledge & Content
  {
    label: 'Knowledge & Content',
    isSection: true,
  },
  {
    icon: Search,
    label: 'Research Intelligence',
    to: '/research',
    hint: 'Explore Reddit research: topics, opinions, questions & gaps',
    subItems: [
      {
        label: 'AEO Research',
        to: '/research/aeo',
        icon: Brain,
      },
      {
        label: 'Competitive Benchmark',
        to: '/research/competitive-benchmark',
        icon: Trophy,
      },
      {
        label: 'Question Wheel',
        to: '/research/question-wheel',
        icon: HelpCircle,
      },
      {
        label: 'Data Sources',
        to: '/research/data-sources',
        icon: Database,
      },
      {
        label: 'Attribute Approval',
        to: '/research/attribute-approval',
        icon: CheckSquare,
      },
      {
        label: 'Enrichment Review',
        to: '/research/enrichment-review',
        icon: Shield,
      },
      {
        label: 'Bulk Correction',
        to: '/research/bulk-correction',
        icon: Wrench,
      },
      {
        label: 'Sector Question Wheels',
        to: '/research/sector-wheels',
        icon: MessageCircle,
      },
      {
        label: 'Answer Generation',
        to: '/answer-generation',
        icon: Sparkles,
      },
      {
        label: 'Knowledge',
        to: '/knowledge-base',
        icon: BookOpen,
      },
    ],
  },
  {
    icon: BookOpen,
    label: 'My Knowledge',
    to: '/knowledge-base',
    hint: 'Uploaded content: products, documents, and reviews',
  },
  {
    icon: Sparkles,
    label: 'Discovery',
    to: '/discovery',
    hint: 'Explore products, personas, and questions using natural language',
  },
  {
    icon: HelpCircle,
    label: 'Q&A System',
    to: '/qa-system',
    hint: 'Manage AI-powered questions and answers for your site',
  },
  // Temporarily hidden until improved
  // {
  //   icon: Target,
  //   label: 'Demand Intelligence',
  //   to: '/demand-intelligence',
  //   hint: 'Discover high-demand questions and generate targeted FAQs',
  // },
  {
    icon: FileText,
    label: 'My Site Topics',
    to: '/my-topics',
    hint: 'Topics and themes found on your website',
  },
  {
    icon: Edit3,
    label: 'AI Articles',
    to: '/ai-articles',
    hint: 'AI-generated blog articles from topics',
  },
  {
    icon: FolderOpen,
    label: 'Feed Export',
    to: '/feed-export',
    hint: 'Export enriched product feeds with Q&A data',
    visibleFor: ['product', 'mixed'] as const,
  },
  {
    icon: Settings,
    label: 'Content Settings',
    to: '/settings/content-config',
    hint: 'Configure tone, style, and rules for AI-generated content',
  },
  {
    icon: BookOpen,
    label: 'Domain Knowledge',
    to: '/domain-knowledge',
    hint: 'Manage knowledge rules for AI content generation',
  },
  {
    icon: CheckSquare,
    label: 'Content Review',
    to: '/content-review',
    hint: 'Review and approve AI-generated content',
  },
  {
    icon: Workflow,
    label: 'AEO Pipeline',
    to: '/pipeline',
    hint: 'Automated content generation with validation loops',
  },
  {
    icon: Sparkles,
    label: 'AEO Impact Demo',
    to: '/aeo-impact-demo',
    hint: 'Interactive demo showing AEO element impact on metrics',
  },
  {
    icon: TrendingUp,
    label: 'AEO Impact',
    to: '/aeo-impact',
    hint: 'Measure AEO impact on traffic, rankings, and conversions',
  },
  // Site Tools
  {
    label: 'Site Tools',
    isSection: true,
  },
  {
    icon: Gauge,
    label: 'AEO Readiness',
    to: '/aeo-readiness',
    hint: 'Check site readiness for AEO content generation',
  },
  {
    icon: Wand2,
    label: 'AEO Onboarding',
    to: '/aeo-onboarding',
    hint: 'Wizard to prepare your site for AEO content generation',
  },
  {
    icon: Bot,
    label: 'Robot Diagnostics',
    to: '/robot-diagnostics',
    hint: 'Check how search robots see your site',
  },
  {
    icon: Activity,
    label: 'Crawl Intelligence',
    to: '/crawl-intelligence',
    hint: 'Bot traffic and AEO exposure metrics',
  },
  {
    icon: Globe,
    label: 'Deployment Verification',
    to: '/deployment-verification',
    hint: 'Verify if AEO FAQs are deployed on live pages',
  },
  {
    icon: Briefcase,
    label: 'Executive Dashboard',
    to: '/executive-dashboard',
    hint: 'Strategic value metrics and business impact',
  },
  {
    icon: Factory,
    label: 'AI Factory',
    to: '/factory-dashboard',
    hint: 'Production operations and content generation',
  },
  {
    icon: Target,
    label: 'Pipeline Tracker',
    to: '/pipeline-tracker',
    hint: 'Mission control for content pipeline across sites',
  },
  {
    icon: TrendingUp,
    label: 'Content ROI',
    to: '/content-roi',
    hint: 'Track ROI of AI-optimized content changes',
  },
  {
    icon: Lightbulb,
    label: 'Content Intelligence',
    to: '/content-intelligence',
    hint: 'Expert opportunities, content planner, E-E-A-T map',
  },
  {
    icon: CheckSquare,
    label: 'Content Opportunities',
    to: '/content-opportunities',
    hint: 'Review and approve content generation proposals',
  },
  {
    icon: BarChart2,
    label: 'Google Intelligence',
    to: '/google-intelligence',
    hint: 'Unified view of GSC, Analytics, Ads, and Shopping',
  },
  // Sites & Structure
  {
    label: 'Sites & Structure',
    isSection: true,
  },
  {
    icon: Globe,
    label: 'My Sites',
    to: '/my-sites',
    hint: 'Add, edit, or remove monitored websites',
  },
  {
    icon: Globe,
    label: 'Markets',
    to: '/settings/markets',
    hint: 'Configure target markets for keyword research',
  },
  {
    icon: Zap,
    label: 'Google Integrations',
    to: '/settings/integrations',
    hint: 'Connect Google Search Console, Analytics, and Ads',
  },
  {
    icon: ShoppingBag,
    label: 'Shopify',
    to: '/settings/shopify',
    hint: 'Connect your Shopify store for catalog sync and AEO publishing',
  },
  {
    icon: Activity,
    label: 'Crawl Monitor',
    to: '/crawl-monitor',
    hint: 'Monitor and manage crawl sessions for your sites',
  },
  {
    icon: Search,
    label: 'SEO Tracking',
    to: '/seo-tracking',
    hint: 'Track keyword rankings and SERP performance',
  },
  {
    icon: TrendingUp,
    label: 'Product Visibility',
    to: '/seo/visibility',
    hint: 'Track product rankings in Google search results',
    visibleFor: ['product', 'mixed'] as const,
  },
  // Developer
  {
    label: 'Developer',
    isSection: true,
  },
  {
    icon: Key,
    label: 'API Management',
    to: '/developer/api',
    hint: 'Connect PageMind to your other tools',
  },
  // Administration
  {
    label: 'Administration',
    isSection: true,
  },
  {
    icon: User,
    label: 'Account',
    to: '/account',
    hint: 'Your personal settings and preferences',
  },
  {
    icon: CreditCard,
    label: 'Licensing',
    to: '/account/licensing',
    hint: 'Manage your plan and payment details',
  },
  // Help & Learning
  {
    label: 'Help & Learning',
    isSection: true,
  },
  {
    icon: BookOpen,
    label: 'Learning Center',
    to: '/help',
    hint: 'Guides, tutorials, and best practices',
    subItems: [
      {
        label: 'FAQ',
        to: '/help/faq',
        icon: HelpCircle,
      },
      {
        label: 'User Guides',
        to: '/help/guides',
        icon: FileText,
      },
    ],
  },
];

// Content Manager Menu Items - Simplified workflow-focused navigation
const contentManagerItems: SidebarItem[] = [
  // Dashboard
  {
    label: 'Dashboard',
    isSection: true,
  },
  {
    icon: Home,
    label: 'Home',
    to: '/dashboard',
    hint: 'Your command center - see site health and action items',
  },
  // About the Business
  {
    label: 'About the Business',
    isSection: true,
  },
  {
    icon: TrendingUp,
    label: 'The Sector',
    to: '/about-business/sector',
    hint: 'Industry insights and trends for your business',
  },
  {
    icon: Package,
    label: 'The Offering',
    to: '/about-business/offering',
    hint: 'View and enrich your product catalog',
  },
  {
    icon: Sparkles,
    label: 'Discovery',
    to: '/discovery',
    hint: 'Natural language search across your products and knowledge',
  },
  {
    icon: DollarSign,
    label: 'Demand Research',
    to: '/demand-research',
    hint: 'Category demand vs content readiness analysis',
  },
  {
    icon: MessageCircle,
    label: 'Conversation Intel',
    to: '/conversation-intelligence',
    hint: 'Community conversations, sentiment, opportunities & experts',
  },
  // Services (service & mixed sites only)
  {
    icon: Briefcase,
    label: 'Services',
    visibleFor: ['service', 'mixed'] as const,
    subItems: [
      { label: 'Service Catalog', to: '/service/catalog', icon: Package },
      { label: 'Bookings', to: '/service/bookings', icon: Calendar },
      { label: 'Integrations', to: '/service/integrations', icon: Workflow },
      { label: 'Lead Inbox', to: '/service/leads', icon: Inbox },
    ],
  },
  // Temporarily hidden until improved
  // {
  //   icon: Target,
  //   label: 'Demand Intelligence',
  //   to: '/demand-intelligence',
  //   hint: 'Discover high-demand questions and generate targeted FAQs',
  // },
  // Agents
  {
    label: 'Agents',
    isSection: true,
  },
  {
    icon: UserCircle,
    label: 'Meet the Crew',
    to: '/crew',
    hint: 'Meet your AI assistants and see what they\'re doing',
  },
  // Review & Approval - Primary workflow for Content Managers
  {
    label: 'Review & Approval',
    isSection: true,
  },
  {
    icon: CheckSquare,
    label: 'Content Review',
    to: '/content-review',
    hint: 'Review and approve AI-generated AEO content',
  },
  {
    icon: Shield,
    label: 'Enrichment Review',
    to: '/research/enrichment-review',
    hint: 'Review and approve AI-enriched products',
  },
  {
    icon: Wrench,
    label: 'Bulk Correction',
    to: '/research/bulk-correction',
    hint: 'Search and fix product data in bulk using natural language',
  },
  {
    icon: CheckSquare,
    label: 'Attribute Approval',
    to: '/research/attribute-approval',
    hint: 'Approve or filter feed attributes for content generation',
  },
  {
    icon: MessageSquare,
    label: 'My Suggestions',
    to: '/my-suggestions',
    hint: 'Track your submitted feedback and bug reports',
  },
  {
    icon: Workflow,
    label: 'AEO Pipeline',
    to: '/pipeline',
    hint: 'Automated content generation with validation loops',
  },
  {
    icon: TrendingUp,
    label: 'AEO Impact',
    to: '/aeo-impact',
    hint: 'Measure AEO impact on traffic, rankings, and conversions',
  },
  // Site Tools
  {
    label: 'Site Tools',
    isSection: true,
  },
  {
    icon: Gauge,
    label: 'AEO Readiness',
    to: '/aeo-readiness',
    hint: 'Check site readiness for AEO content generation',
  },
  {
    icon: Wand2,
    label: 'AEO Onboarding',
    to: '/aeo-onboarding',
    hint: 'Wizard to prepare your site for AEO content generation',
  },
  {
    icon: Bot,
    label: 'Robot Diagnostics',
    to: '/robot-diagnostics',
    hint: 'Check how search robots see your site',
  },
  {
    icon: Activity,
    label: 'Crawl Intelligence',
    to: '/crawl-intelligence',
    hint: 'Bot traffic and AEO exposure metrics',
  },
  {
    icon: Globe,
    label: 'Deployment Verification',
    to: '/deployment-verification',
    hint: 'Verify if AEO FAQs are deployed on live pages',
  },
  {
    icon: TrendingUp,
    label: 'Product Visibility',
    to: '/seo/visibility',
    hint: 'Track product rankings in Google search results',
    visibleFor: ['product', 'mixed'] as const,
  },
  {
    icon: Briefcase,
    label: 'Executive Dashboard',
    to: '/executive-dashboard',
    hint: 'Strategic value metrics and business impact',
  },
  {
    icon: Factory,
    label: 'AI Factory',
    to: '/factory-dashboard',
    hint: 'Production operations and content generation',
  },
  {
    icon: Target,
    label: 'Pipeline Tracker',
    to: '/pipeline-tracker',
    hint: 'Mission control for content pipeline across sites',
  },
  {
    icon: BarChart2,
    label: 'Google Intelligence',
    to: '/google-intelligence',
    hint: 'Unified view of GSC, Analytics, Ads, and Shopping',
  },
  // Content Creation
  {
    label: 'Content Creation',
    isSection: true,
  },
  {
    icon: Settings,
    label: 'Content Settings',
    to: '/settings/content-config',
    hint: 'Configure tone, style, and rules for AI-generated content',
  },
  {
    icon: BookOpen,
    label: 'Domain Knowledge',
    to: '/domain-knowledge',
    hint: 'Manage knowledge rules for AI content generation',
  },
  {
    icon: Video,
    label: 'AI Journalist',
    to: '/admin/journalists',
    hint: 'Manage AI journalists for interviews',
  },
  {
    icon: MessageSquare,
    label: 'Content Interviews',
    to: '/admin/interviews',
    hint: 'View and manage content interviews',
  },
  {
    icon: Users,
    label: 'Experts Management',
    to: '/admin/experts',
    hint: 'Manage industry experts for content creation',
  },
  {
    icon: Edit3,
    label: 'AI Articles',
    to: '/ai-articles',
    hint: 'Create AI-powered blog content',
  },
  // Research
  {
    label: 'Research',
    isSection: true,
  },
  {
    icon: FileText,
    label: 'My Site Topics',
    to: '/my-topics',
    hint: 'Research topics relevant to your business',
  },
  {
    icon: Trophy,
    label: 'Competitive Benchmark',
    to: '/research/competitive-benchmark',
    hint: 'Compare your content against competitors',
  },
  // Exports
  {
    label: 'Exports',
    isSection: true,
  },
  {
    icon: FolderOpen,
    label: 'Feed Export',
    to: '/feed-export',
    hint: 'Export enriched product data',
    visibleFor: ['product', 'mixed'] as const,
  },
  {
    icon: Upload,
    label: 'Feed Imports',
    to: '/feed-imports',
    hint: 'Import and manage product feeds',
    visibleFor: ['product', 'mixed'] as const,
  },
  {
    icon: Activity,
    label: 'Processing Jobs',
    to: '/processing-jobs',
    hint: 'Monitor feed processing status and costs',
  },
  // Account
  {
    label: 'Account',
    isSection: true,
  },
  {
    icon: User,
    label: 'My Account',
    to: '/account',
    hint: 'Your personal settings and preferences',
  },
  // Help & Learning
  {
    label: 'Help & Learning',
    isSection: true,
  },
  {
    icon: BookOpen,
    label: 'Learning Center',
    to: '/help',
    hint: 'Guides, tutorials, and best practices',
    subItems: [
      {
        label: 'FAQ',
        to: '/help/faq',
        icon: HelpCircle,
      },
      {
        label: 'User Guides',
        to: '/help/guides',
        icon: FileText,
      },
    ],
  },
];

// Super Admin Menu Items
const superAdminItems: SidebarItem[] = [
  // Dashboard
  {
    label: 'Dashboard',
    isSection: true,
  },
  {
    icon: Home,
    label: 'Home',
    to: '/dashboard',
    hint: 'Your command center - see how your site is performing',
  },
  // About the Business
  {
    label: 'About the Business',
    isSection: true,
  },
  {
    icon: TrendingUp,
    label: 'The Sector',
    to: '/about-business/sector',
    hint: 'Industry insights and trends for your business',
  },
  {
    icon: Package,
    label: 'The Offering',
    to: '/about-business/offering',
    hint: 'Your products, services, and content catalog',
  },
  {
    icon: Sparkles,
    label: 'Discovery',
    to: '/discovery',
    hint: 'Natural language search across your products and knowledge',
  },
  {
    icon: DollarSign,
    label: 'Demand Research',
    to: '/demand-research',
    hint: 'Category demand vs content readiness analysis',
  },
  {
    icon: MessageCircle,
    label: 'Conversation Intel',
    to: '/conversation-intelligence',
    hint: 'Community conversations, sentiment, opportunities & experts',
  },
  // Services (service & mixed sites only)
  {
    icon: Briefcase,
    label: 'Services',
    visibleFor: ['service', 'mixed'] as const,
    subItems: [
      { label: 'Service Catalog', to: '/service/catalog', icon: Package },
      { label: 'Bookings', to: '/service/bookings', icon: Calendar },
      { label: 'Integrations', to: '/service/integrations', icon: Workflow },
      { label: 'Lead Inbox', to: '/service/leads', icon: Inbox },
    ],
  },
  // Agents
  {
    label: 'Agents',
    isSection: true,
  },
  {
    icon: UserCircle,
    label: 'Meet the Crew',
    to: '/crew',
    hint: 'Meet your AI assistants and see what they\'re doing',
  },
  {
    icon: MessageSquare,
    label: 'Agents Insights',
    to: '/insights/messages',
    hint: 'Discover what your AI agents learned about visitors',
  },
  {
    icon: Inbox,
    label: 'Action Inbox',
    to: '/insights/inbox',
    hint: 'Review and act on AI agent suggestions',
  },
  {
    icon: MessageCircle,
    label: 'Agent Conversations',
    to: '/agent-conversations',
    hint: 'Read actual conversations between visitors and AI',
  },
  // Customers & Growth
  {
    label: 'Customers & Growth',
    isSection: true,
  },
  {
    icon: BarChart2,
    label: 'Analytics & Chatbot',
    to: '/analytics',
    hint: 'Track web traffic, chatbot conversations, and engagement metrics',
  },
  {
    icon: Users,
    label: 'Customer Segments',
    to: '/analytics/customer-segments',
    hint: 'See who your visitors are - buyers, browsers, researchers',
  },
  {
    icon: Layers,
    label: 'Segment Management',
    to: '/analytics/segment-management',
    hint: 'Define and customize your visitor groups',
  },
  {
    icon: BarChart2,
    label: 'Segment Analytics',
    to: '/analytics/segment-analytics',
    hint: 'Compare how different visitor groups perform',
  },
  {
    icon: Layout,
    label: 'Experiences',
    to: '/session-replays',
    hint: 'Watch how real visitors navigate your site',
  },
  {
    icon: Brain,
    label: 'Behavior',
    to: '/behavior',
    hint: 'Understand what visitors do and why they do it',
  },
  {
    icon: TrendingUp,
    label: 'Growth',
    to: '/growth',
    hint: 'Find opportunities to grow your business',
  },
  // Knowledge & Content
  {
    label: 'Knowledge & Content',
    isSection: true,
  },
  {
    icon: Search,
    label: 'Research Intelligence',
    to: '/research',
    hint: 'Explore Reddit research: topics, opinions, questions & gaps',
    subItems: [
      {
        label: 'AEO Research',
        to: '/research/aeo',
        icon: Brain,
      },
      {
        label: 'Competitive Benchmark',
        to: '/research/competitive-benchmark',
        icon: Trophy,
      },
      {
        label: 'Question Wheel',
        to: '/research/question-wheel',
        icon: HelpCircle,
      },
      {
        label: 'Data Sources',
        to: '/research/data-sources',
        icon: Database,
      },
      {
        label: 'Attribute Approval',
        to: '/research/attribute-approval',
        icon: CheckSquare,
      },
      {
        label: 'Enrichment Review',
        to: '/research/enrichment-review',
        icon: Shield,
      },
      {
        label: 'Bulk Correction',
        to: '/research/bulk-correction',
        icon: Wrench,
      },
      {
        label: 'Sector Question Wheels',
        to: '/research/sector-wheels',
        icon: MessageCircle,
      },
      {
        label: 'Answer Generation',
        to: '/answer-generation',
        icon: Sparkles,
      },
      {
        label: 'Knowledge',
        to: '/knowledge-base',
        icon: BookOpen,
      },
    ],
  },
  {
    icon: BookOpen,
    label: 'My Knowledge',
    to: '/knowledge-base',
    hint: 'Uploaded content: products, documents, and reviews',
  },
  {
    icon: Sparkles,
    label: 'Discovery',
    to: '/discovery',
    hint: 'Explore products, personas, and questions using natural language',
  },
  {
    icon: HelpCircle,
    label: 'Q&A System',
    to: '/qa-system',
    hint: 'Manage AI-powered questions and answers for your site',
  },
  // Temporarily hidden until improved
  // {
  //   icon: Target,
  //   label: 'Demand Intelligence',
  //   to: '/demand-intelligence',
  //   hint: 'Discover high-demand questions and generate targeted FAQs',
  // },
  {
    icon: FileText,
    label: 'My Site Topics',
    to: '/my-topics',
    hint: 'Topics and themes found on your website',
  },
  {
    icon: Edit3,
    label: 'AI Articles',
    to: '/ai-articles',
    hint: 'AI-generated blog articles from topics',
  },
  {
    icon: FolderOpen,
    label: 'Feed Export',
    to: '/feed-export',
    hint: 'Export enriched product feeds with Q&A data',
    visibleFor: ['product', 'mixed'] as const,
  },
  {
    icon: Upload,
    label: 'Feed Imports',
    to: '/feed-imports',
    hint: 'Import and manage product feeds',
    visibleFor: ['product', 'mixed'] as const,
  },
  {
    icon: Settings,
    label: 'Content Settings',
    to: '/settings/content-config',
    hint: 'Configure tone, style, and rules for AI-generated content',
  },
  {
    icon: BookOpen,
    label: 'Domain Knowledge',
    to: '/domain-knowledge',
    hint: 'Manage knowledge rules for AI content generation',
  },
  {
    icon: CheckSquare,
    label: 'Content Review',
    to: '/content-review',
    hint: 'Review and approve AI-generated content',
  },
  {
    icon: Workflow,
    label: 'AEO Pipeline',
    to: '/pipeline',
    hint: 'Automated content generation with validation loops',
  },
  {
    icon: Sparkles,
    label: 'AEO Impact Demo',
    to: '/aeo-impact-demo',
    hint: 'Interactive demo showing AEO element impact on metrics',
  },
  {
    icon: TrendingUp,
    label: 'AEO Impact',
    to: '/aeo-impact',
    hint: 'Measure AEO impact on traffic, rankings, and conversions',
  },
  // Site Tools
  {
    label: 'Site Tools',
    isSection: true,
  },
  {
    icon: Gauge,
    label: 'AEO Readiness',
    to: '/aeo-readiness',
    hint: 'Check site readiness for AEO content generation',
  },
  {
    icon: Wand2,
    label: 'AEO Onboarding',
    to: '/aeo-onboarding',
    hint: 'Wizard to prepare your site for AEO content generation',
  },
  {
    icon: Bot,
    label: 'Robot Diagnostics',
    to: '/robot-diagnostics',
    hint: 'Check how search robots see your site',
  },
  {
    icon: Activity,
    label: 'Crawl Intelligence',
    to: '/crawl-intelligence',
    hint: 'Bot traffic and AEO exposure metrics',
  },
  {
    icon: Globe,
    label: 'Deployment Verification',
    to: '/deployment-verification',
    hint: 'Verify if AEO FAQs are deployed on live pages',
  },
  {
    icon: Briefcase,
    label: 'Executive Dashboard',
    to: '/executive-dashboard',
    hint: 'Strategic value metrics and business impact',
  },
  {
    icon: Factory,
    label: 'AI Factory',
    to: '/factory-dashboard',
    hint: 'Production operations and content generation',
  },
  {
    icon: Target,
    label: 'Pipeline Tracker',
    to: '/pipeline-tracker',
    hint: 'Mission control for content pipeline across sites',
  },
  {
    icon: TrendingUp,
    label: 'Content ROI',
    to: '/content-roi',
    hint: 'Track ROI of AI-optimized content changes',
  },
  {
    icon: Lightbulb,
    label: 'Content Intelligence',
    to: '/content-intelligence',
    hint: 'Expert opportunities, content planner, E-E-A-T map',
  },
  {
    icon: CheckSquare,
    label: 'Content Opportunities',
    to: '/content-opportunities',
    hint: 'Review and approve content generation proposals',
  },
  {
    icon: BarChart2,
    label: 'Google Intelligence',
    to: '/google-intelligence',
    hint: 'Unified view of GSC, Analytics, Ads, and Shopping',
  },
  // Sites & Structure
  {
    label: 'Sites & Structure',
    isSection: true,
  },
  {
    icon: Globe,
    label: 'My Sites',
    to: '/my-sites',
    hint: 'Add, edit, or remove monitored websites',
  },
  {
    icon: Globe,
    label: 'Markets',
    to: '/settings/markets',
    hint: 'Configure target markets for keyword research',
  },
  {
    icon: Zap,
    label: 'Google Integrations',
    to: '/settings/integrations',
    hint: 'Connect Google Search Console, Analytics, and Ads',
  },
  {
    icon: ShoppingBag,
    label: 'Shopify',
    to: '/settings/shopify',
    hint: 'Connect your Shopify store for catalog sync and AEO publishing',
  },
  {
    icon: Activity,
    label: 'Crawl Monitor',
    to: '/crawl-monitor',
    hint: 'Monitor and manage crawl sessions for your sites',
  },
  {
    icon: Search,
    label: 'SEO Tracking',
    to: '/seo-tracking',
    hint: 'Track keyword rankings and SERP performance',
  },
  {
    icon: TrendingUp,
    label: 'Product Visibility',
    to: '/seo/visibility',
    hint: 'Track product rankings in Google search results',
    visibleFor: ['product', 'mixed'] as const,
  },
  // Developer
  {
    label: 'Developer',
    isSection: true,
  },
  {
    icon: Key,
    label: 'API Management',
    to: '/developer/api',
    hint: 'Connect PageMind to your other tools',
  },
  // Administration
  {
    label: 'Administration',
    isSection: true,
  },
  {
    icon: User,
    label: 'Account',
    to: '/account',
    hint: 'Your personal settings and preferences',
  },
  {
    icon: CreditCard,
    label: 'Licensing',
    to: '/account/licensing',
    hint: 'Manage your plan and payment details',
  },
  {
    icon: Shield,
    label: 'Super Admin',
    to: '/admin',
    hint: 'Advanced controls for system administrators',
    subItems: [
      // Setup & Structure
      {
        label: 'Setup & Structure',
        isHeader: true,
      },
      {
        icon: Globe,
        label: 'Sites Manager',
        to: '/admin/sites',
      },
      {
        icon: Building2,
        label: 'Sectors',
        to: '/admin/sectors',
      },
      {
        icon: Sparkles,
        label: 'Research Management',
        to: '/admin/research-management',
      },
      {
        icon: Layers,
        label: 'Category Knowledge',
        to: '/admin/category-research',
      },
      {
        icon: CheckSquare,
        label: 'Master Topics Approval',
        to: '/admin/topics',
      },
      // Agents
      {
        label: 'Agents',
        isHeader: true,
      },
      {
        icon: Grid,
        label: 'Agent Dashboard',
        to: '/agents/dashboard',
      },
      {
        icon: Monitor,
        label: 'Agent Monitoring',
        to: '/admin/agent-monitoring',
      },
      {
        icon: CreditCard,
        label: 'License Management',
        to: '/admin/licenses',
      },
      {
        icon: CreditCard,
        label: 'Subscription Management',
        to: '/admin/subscriptions',
      },
      {
        icon: Globe,
        label: 'Source Domains',
        to: '/admin/source-domains',
      },
      {
        icon: Image,
        label: 'Image Generation',
        to: '/admin/image-generation',
      },
      {
        icon: Users,
        label: 'AI Journalists',
        to: '/admin/journalists',
      },
      {
        icon: Video,
        label: 'Content Interviews',
        to: '/admin/interviews',
      },
      {
        icon: Users,
        label: 'Experts Management',
        to: '/admin/experts',
      },
      {
        icon: Users,
        label: 'Waitlist Management',
        to: '/admin/waitlist',
      },
      {
        icon: Shield,
        label: 'Site Permissions',
        to: '/admin/site-permissions',
      },
      // Live Operations
      {
        label: 'Live Operations',
        isHeader: true,
      },
      {
        icon: Activity,
        label: 'Customer Simulator',
        to: '/admin/customer-simulator',
      },
      {
        icon: Activity,
        label: 'Active Sessions',
        to: '/admin/active-sessions',
      },
      {
        icon: PlayCircle,
        label: 'Session Monitoring',
        to: '/monitoring',
      },
      // Knowledge & Logs
      {
        label: 'Knowledge & Logs',
        isHeader: true,
      },
      {
        icon: MessageSquare,
        label: 'Platform Suggestions',
        to: '/admin/suggestions',
      },
      {
        icon: BookOpen,
        label: 'Research Documents',
        to: '/admin/research-documents',
      },
      {
        icon: Database,
        label: 'Chatbot Logs',
        to: '/admin/chatbot-logs',
      },
      {
        icon: Activity,
        label: 'Chatbot Performance',
        to: '/admin/chatbot-performance',
      },
      {
        icon: Activity,
        label: 'Search Performance',
        to: '/admin/search-performance',
      },
      // Platform Health
      {
        label: 'Platform Health',
        isHeader: true,
      },
      {
        icon: BarChart2,
        label: 'Grafana',
        to: '/admin/grafana',
      },
      {
        icon: Activity,
        label: 'Prometheus',
        to: '/admin/prometheus',
      },
      {
        icon: Database,
        label: 'ETL Monitoring',
        to: '/analytics/etl',
      },
      {
        icon: Activity,
        label: 'Processing Jobs',
        to: '/processing-jobs',
      },
      {
        icon: MessageSquare,
        label: 'Kafka Queue Monitor',
        to: '/admin/kafka-monitoring',
      },
      {
        icon: Trash2,
        label: 'Data Management',
        to: '/admin/data-management',
      },
      // Developer Tools
      {
        label: 'Developer Tools',
        isHeader: true,
      },
      {
        icon: Code,
        label: 'Tools & Docs',
        to: '/admin/developer-tools',
      },
    ],
  },
  // Help & Learning
  {
    label: 'Help & Learning',
    isSection: true,
  },
  {
    icon: BookOpen,
    label: 'Learning Center',
    to: '/help',
    hint: 'Guides, tutorials, and best practices',
    subItems: [
      {
        label: 'FAQ',
        to: '/help/faq',
        icon: HelpCircle,
      },
      {
        label: 'User Guides',
        to: '/help/guides',
        icon: FileText,
      },
    ],
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [superAdminOpen, setSuperAdminOpen] = React.useState(true);
  const [sectorsOpen, setSectorsOpen] = React.useState(false);
  
  const isSuperAdmin = user?.role === 'super_admin';
  const isContentManager = user?.role === 'site_content_manager';

  // Use appropriate menu items based on user role
  const offeringType = useOfferingType();
  let sidebarItems = isSuperAdmin
    ? superAdminItems
    : isContentManager
      ? contentManagerItems
      : siteOwnerItems;
  sidebarItems = filterSidebarByOfferingType(sidebarItems, offeringType);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Use standard logo for consistency
  const logoSrc = "/pagemind-logo.png?v=8";

  return (
    <aside 
      className={`bg-sidebar dark:bg-sidebar border-r border-border flex flex-col transition-all duration-300 relative z-40 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div className="h-16 flex items-center justify-center px-4 border-b border-border">
        {!collapsed ? (
          <img 
            src={logoSrc} 
            alt="PageMind" 
            className="h-10 w-auto object-contain"
          />
        ) : (
          <img 
            src="/pagemind-icon-transparent.png?v=7" 
            alt="PM" 
            className="h-8 w-8 object-contain"
          />
        )}
      </div>
      
      <div className="flex-1 py-4 overflow-y-auto scrollbar-hidden">
        <nav>
          <ul className="space-y-1 px-2">
            {sidebarItems.map((item, index) => (
              <li key={`${item.label}-${index}`}>
                {item.isSection ? (
                  !collapsed && (
                    <div className={`font-medium text-xs uppercase text-muted-foreground px-3 ${index > 0 ? 'pt-4' : ''} pb-2`}>
                      {item.label}
                    </div>
                  )
                ) : (
                  <>
                    {(item.label === 'Super Admin' || item.label === 'Sectors') ? (
                      <Link
                        to={item.to || '#'}
                        onClick={(e) => {
                          e.preventDefault();
                          if (item.label === 'Super Admin') {
                            navigate(item.to || '#');
                            setSuperAdminOpen(!superAdminOpen);
                          } else if (item.label === 'Sectors') {
                            setSectorsOpen(!sectorsOpen);
                          }
                        }}
                        className={`group relative flex items-center gap-3 px-3 py-2 rounded-md transition-colors cursor-pointer select-none ${
                          item.to && isActive(item.to)
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        } ${collapsed ? "justify-center" : ""}`}
                      >
                        {item.icon && <item.icon className={`h-5 w-5 flex-shrink-0 ${collapsed ? 'mx-auto' : ''}`} />}
                        {!collapsed && <span className="text-sm font-medium flex-1" title={item.hint}>{item.label}</span>}
                      </Link>
                    ) : (
                      <Link
                        to={item.to || '#'}
                        className={`group relative flex items-center gap-3 px-3 py-2 rounded-md transition-colors cursor-pointer select-none ${
                          item.to && isActive(item.to)
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        } ${collapsed ? "justify-center" : ""}`}
                      >
                        {item.icon && <item.icon className={`h-5 w-5 flex-shrink-0 ${collapsed ? 'mx-auto' : ''}`} />}
                        {!collapsed && <span className="text-sm font-medium flex-1" title={item.hint}>{item.label}</span>}
                      </Link>
                    )}
                    {!collapsed && item.subItems && ((item.label === 'Super Admin' && superAdminOpen) || (item.label === 'Sectors' && sectorsOpen) || (item.to && isActive(item.to))) && (
                  <ul className="mt-1 space-y-1">
                    {item.subItems.map((subItem) => (
                      <li key={subItem.label}>
                        <Link
                          to={subItem.to}
                          onClick={(e) => {
                            if (!subItem.isHeader) {
                              e.preventDefault();
                              navigate(subItem.to);
                            }
                          }}
                          className={`block px-3 py-1 text-sm rounded-md transition-colors select-none ${
                            subItem.isHeader
                              ? "hover:bg-transparent cursor-default pointer-events-none"
                              : isActive(subItem.to)
                                ? "bg-sidebar-accent text-sidebar-accent-foreground cursor-pointer"
                                : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground cursor-pointer"
                          }`}
                        >
                          {subItem.isHeader ? (
                            <div className="font-medium text-xs uppercase text-gray-400 pb-1 pt-3">
                              {subItem.label}
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {subItem.avatar && (
                                  <img 
                                    src={subItem.avatar} 
                                    alt={subItem.label}
                                    className="h-5 w-5 rounded-full object-cover"
                                  />
                                )}
                                {subItem.icon && (
                                  <subItem.icon className="h-4 w-4 text-gray-500" />
                                )}
                                <span>{subItem.label}</span>
                              </div>
                              {subItem.badge && (
                                <span 
                                  className={`ml-2 flex items-center justify-center min-w-5 h-5 text-xs rounded-full text-white
                                    ${subItem.badge.variant === 'blue' ? 'bg-blue-500' : 
                                      subItem.badge.variant === 'green' ? 'bg-green-500' : 
                                      subItem.badge.variant === 'red' ? 'bg-red-500' : 
                                      'bg-yellow-500'
                                    }`}
                                >
                                  {subItem.badge.count}
                                </span>
                              )}
                            </div>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                  </>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>
      
      <div className="p-4 border-t border-border">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center p-2 rounded-md hover:bg-sidebar-accent text-sidebar-foreground hover:text-sidebar-accent-foreground"
        >
          {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      </div>
    </aside>
  );
};
